import { useTheme } from "@mui/material";
import { Text, View } from "@react-pdf/renderer";
import { PDFStyleConfigs } from "../../../configs";
import { PDFTableRow } from "../table-row";
import { PDFTableCell } from "../table-cell";
import { ProductRecommendationDTO } from "@models/platform-analysis/entities/steps/summary";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";
import { RecommendationLabel } from "../recommendation-label";
import {
  checkContainsMultipleLinesRecommendationBadge,
  shouldShowRecommendationLabel,
} from "../../../utils";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";

export const PDFTableHeaderWithoutLogo = ({
  selectedProducts,
  title,
  titleFontSize,
  showSubProductName = false,
  staticFirstCellWidth,
  staticFollowingCellWidth,
  forceShowBorderBottom = false,
  imageSize = 35,
  productRecommendations,
  isReview,
}: {
  selectedProducts?: SelectedProductDTO[];
  title?: string;
  titleFontSize?: number;
  showSubProductName?: boolean;
  staticFirstCellWidth?: number;
  staticFollowingCellWidth?: number;
  forceShowBorderBottom?: boolean;
  imageSize?: number;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  isReview?: boolean;
}) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();
  const firstCellWidth =
    staticFirstCellWidth ?? PDFStyleConfigs.TableFirstCellWidth;
  const followingCellWidth =
    staticFollowingCellWidth ?? PDFStyleConfigs.TableCellWidth;

  const containsMultipleLinesBadge =
    checkContainsMultipleLinesRecommendationBadge({
      selectedProducts,
      productRecommendations,
    });

  return (
    <PDFTableRow
      type={
        isReview && shouldShowRecommendationLabel(productRecommendations)
          ? "HeaderDoubleLineNoLogoWithRecommendation"
          : "HeaderDoubleLineNoLogo"
      }
      additionHeight={
        containsMultipleLinesBadge
          ? PDFStyleConfigs.TableRowRecommendationHeight
          : 0
      }
      forceShowBorderBottom={forceShowBorderBottom}
    >
      <PDFTableCell width={firstCellWidth}>
        <Text
          style={{
            ...typographyStyle.body2,
            color: palette.text.primary,
            fontWeight: "bold",
          }}
        >
          {title}
        </Text>
      </PDFTableCell>

      {selectedProducts?.map((product, index) => (
        <View
          key={index}
          style={{
            width: followingCellWidth,
            maxWidth: followingCellWidth,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderColor: "#7C8493",
            borderRightWidth: 1,
            backgroundColor: "transparent",
            position: "relative",
          }}
        >
          {product.isProductMain && (
            <Text
              style={{
                ...typographyStyle.body2,
                alignItems: "center",
                borderRadius: 10,
                borderWidth: 0.5,
                borderColor: "#FFAC8A",
                backgroundColor: "#FFE3D8",
                fontWeight: "bold",
                color: "#E84D1E",
                position: "absolute",
                top: 1,
                right: 1,
                fontSize: 5,
                paddingTop: 2,
                paddingRight: 1,
                paddingLeft: 2,
              }}
            >
              {isReview ? "Current" : "Main"}
            </Text>
          )}

          <View
            style={{
              flexDirection: "column",
              alignItems: "center",
              justifyContent: showSubProductName ? "flex-start" : undefined,
              width: "100%",
              height: "100%",
            }}
          >
            <View
              style={{
                flexDirection: "column",
                alignItems: "center",
                justifyContent: showSubProductName ? "center" : undefined,
                height: PDFStyleConfigs.TableRowHeaderDoubleLineNoLogo,
                width: "100%",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontWeight: "bold",
                  textAlign: "center",
                  marginTop: imageSize,
                  fontSize: titleFontSize
                    ? titleFontSize
                    : typographyStyle.body2.fontSize,
                }}
              >
                {product?.productName}
              </Text>

              {showSubProductName && product?.subProductName && (
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: titleFontSize
                      ? titleFontSize
                      : typographyStyle.body2.fontSize,
                    maxHeight: 20,
                  }}
                >
                  {product?.subProductName}
                </Text>
              )}
            </View>
            <RecommendationLabel
              product={product}
              productRecommendations={productRecommendations}
              shouldIncreaseHeight={containsMultipleLinesBadge}
            />
          </View>
        </View>
      ))}
    </PDFTableRow>
  );
};
